/**
 * ProfilePanel — web port of the **Settings** half of
 * `apps/WunderGuide/components/SettingsDrawer.tsx`. RN structure:
 *   UserHeader → Vision (Manifesto) → AppSettings (Language, Notifications)
 *               → Legal (Terms, Privacy) → Footer (version + © Dodecki Labs)
 *
 * Account / wallet (balance card, top-ups, redeem code, active passes,
 * activity history, delete-account, sign-out) lives in `WalletContent.tsx`,
 * which mirrors RN's separate Account modal — DO NOT merge them here.
 */
import React, { useState } from 'react';
import { useUserContext } from '../../context/UserContext';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { useDrawer } from '../../context/DrawerContext';
import WebViewModal from '../../common/WebViewModal';
import packageJson from '../../../package.json';
import envConfig from '../../../config/env.json';
import './ProfilePanel.css';

const ProfilePanel: React.FC = () => {
  const { user } = useUserContext();
  const { isGuest } = useAuth();
  const { t, Logo, selectedLanguage } = useLanguage();
  const { openDrawer } = useDrawer();
  const [legalModal, setLegalModal] = useState<{
    kind: 'manifesto' | 'terms' | 'privacy';
    url: string;
    title: string;
  } | null>(null);

  const openLegal = (kind: 'manifesto' | 'terms' | 'privacy') => {
    const path = kind === 'terms' ? 'tos' : kind;
    const title =
      kind === 'manifesto'
        ? t('MANIFESTO') || 'Manifesto'
        : kind === 'terms'
          ? t('TERMS') || 'Terms of service'
          : t('PRIVACY') || 'Privacy policy';
    setLegalModal({
      kind,
      title,
      url: `https://wunderguide.com/${path}/?utm_source=wunderguide_web&lang=${selectedLanguage}`,
    });
  };
  const env = (envConfig.ENV || '').toLowerCase();
  const showEnvSuffix = env && env !== 'prod' && env !== 'production';
  const versionText = `v${packageJson.version}${showEnvSuffix ? ` ${envConfig.ENV}` : ''}`;

  return (
    <div className="wg-profile">
      {/* User header — opens Account modal (Wallet drawer) on press, mirroring
          RN's `setShowUserModal(true)`. */}
      {!isGuest && (
        <button
          type="button"
          className="wg-profile__user"
          onClick={() => openDrawer('wallet')}
        >
          <div className="wg-profile__avatar">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" />
            ) : (
              <span className="wg-profile__avatar-initials">
                {(
                  user?.firstName?.[0] ??
                  user?.emailAddress?.[0] ??
                  '?'
                ).toUpperCase()}
              </span>
            )}
          </div>
          <div className="wg-profile__user-text">
            <div className="wg-profile__user-name">
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                user?.emailAddress ||
                t('ACCOUNT') ||
                'Account'}
            </div>
            {user?.emailAddress && (
              <div className="wg-profile__user-email">{user.emailAddress}</div>
            )}
          </div>
          <span className="wg-profile__row-chevron">
            <ChevronIcon />
          </span>
        </button>
      )}

      {/* Vision */}
      <div className="wg-profile__section">
        <div className="wg-profile__section-title">
          {t('OUR_VISION') || 'Our vision'}
        </div>
        <Row
          icon={<SparklesIcon />}
          label={t('MANIFESTO') || 'Manifesto'}
          onClick={() => openLegal('manifesto')}
        />
      </div>

      {/* App settings */}
      <div className="wg-profile__section">
        <div className="wg-profile__section-title">
          {t('APP_SETTINGS') || 'App settings'}
        </div>
        <Row
          icon={<GlobeIcon />}
          label={t('LANGUAGE') || 'Language'}
          value={selectedLanguage}
          onClick={() => openDrawer('language')}
        />
        <Row
          icon={<BellIcon />}
          label={t('NOTIFICATIONS') || 'Notifications'}
          onClick={() => openDrawer('inbox')}
        />
      </div>

      {/* Legal */}
      <div className="wg-profile__section">
        <div className="wg-profile__section-title">{t('LEGAL') || 'Legal'}</div>
        <Row
          icon={<DocIcon />}
          label={t('TERMS') || 'Terms of service'}
          onClick={() => openLegal('terms')}
        />
        <Row
          icon={<ShieldIcon />}
          label={t('PRIVACY') || 'Privacy policy'}
          onClick={() => openLegal('privacy')}
        />
      </div>

      <div className="wg-profile__footer">
        <div className="wg-profile__footer-app">WunderGuide {versionText}</div>
        <div className="wg-profile__footer-copy">
          © {new Date().getFullYear()} Dodecki Labs LLC
        </div>
        <div className="wg-profile__footer-logo" aria-hidden>
          <Logo />
        </div>
      </div>

      <WebViewModal
        visible={legalModal !== null}
        onClose={() => setLegalModal(null)}
        url={legalModal?.url ?? ''}
        title={legalModal?.title ?? ''}
      />
    </div>
  );
};

export default ProfilePanel;

/* ── Sub-components ──────────────────────────────────────────────────── */

interface RowProps {
  icon: React.ReactNode;
  label: string;
  value?: string;
  onClick?: () => void;
}

const Row: React.FC<RowProps> = ({ icon, label, value, onClick }) => (
  <button type="button" className="wg-profile__row" onClick={onClick}>
    <span className="wg-profile__row-icon">{icon}</span>
    <span className="wg-profile__row-label">{label}</span>
    {value && <span className="wg-profile__row-value">{value}</span>}
    <span className="wg-profile__row-chevron">
      <ChevronIcon />
    </span>
  </button>
);

/* ── Inline icons ───────────────────────────────────────────────────── */

function SparklesIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z" />
      <path d="M5 3v4" />
      <path d="M3 5h4" />
      <path d="M19 17v4" />
      <path d="M17 19h4" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 4 2 5 2 7H4c0-2 2-3 2-7z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
